
import React, { useEffect } from 'react';
import { useTenant } from '../context/TenantContext';
import { getAppMode } from '../utils/config';
import { DOMAIN_CONFIG } from '../config/domainConfig';

interface SEOProps {
  title?: string;
  description?: string;
  keywords?: string[];
  image?: string;
}

export const SEO: React.FC<SEOProps> = ({ title, description, keywords, image }) => {
  const { tenant } = useTenant();
  const appMode = getAppMode();

  // Determine Defaults based on mode
  // 1. If Tenant is loaded, use Tenant Name
  // 2. If Marketplace mode, use SyncZ defaults
  // 3. Fallback to generic

  const defaultTitleSuffix = tenant?.name ? ` | ${tenant.name}` : (appMode === 'MARKETPLACE' ? ' | SyncZ App' : ' | Portfolio Portal');
  const defaultDescription = "Professional services, bookings, and project management.";
  const defaultKeywords = "portfolio, booking, services, professional";
  
  // Construct Final Values
  const finalTitle = title ? `${title}${defaultTitleSuffix}` : (tenant?.name || "Portfolio Portal");
  const finalDesc = description || defaultDescription;
  const finalKeywords = keywords ? keywords.join(', ') : defaultKeywords;
  
  // Use uploaded logo/image or default
  // Important: Ensure we have a fully qualified URL for social sharing
  const finalImage = image?.startsWith('http') 
      ? image 
      : `https://${DOMAIN_CONFIG.SAAS_ROOT}/og-default.jpg`; 

  useEffect(() => {
    // 1. Update Title
    document.title = finalTitle;

    // 2. Helper to update meta tags
    const updateMeta = (name: string, content: string, attribute = 'name') => {
        let element = document.querySelector(`meta[${attribute}="${name}"]`);
        if (!element) {
            element = document.createElement('meta');
            element.setAttribute(attribute, name);
            document.head.appendChild(element);
        }
        element.setAttribute('content', content);
    };

    // 3. Helper for Canonical Link (Crucial for Multi-domain SEO)
    const updateCanonical = (url: string) => {
        let link = document.querySelector("link[rel='canonical']") as HTMLLinkElement;
        if (!link) {
            link = document.createElement('link');
            link.setAttribute('rel', 'canonical');
            document.head.appendChild(link);
        }
        link.setAttribute('href', url);
    };

    // 4. Update Standard Tags
    updateMeta('description', finalDesc);
    updateMeta('keywords', finalKeywords);
    updateMeta('author', tenant?.name || 'SyncZ Platform');

    // 5. Update Open Graph
    updateMeta('og:title', finalTitle, 'property');
    updateMeta('og:description', finalDesc, 'property');
    updateMeta('og:image', finalImage, 'property');
    updateMeta('og:url', window.location.href, 'property');
    updateMeta('og:type', 'website', 'property');

    // 6. Update Twitter
    updateMeta('twitter:card', 'summary_large_image', 'property');
    updateMeta('twitter:title', finalTitle, 'property');
    updateMeta('twitter:description', finalDesc, 'property');
    updateMeta('twitter:image', finalImage, 'property');

    // 7. Update Canonical
    // Remove query params to ensure clean indexing
    const cleanUrl = window.location.origin + window.location.pathname;
    updateCanonical(cleanUrl);

  }, [finalTitle, finalDesc, finalImage, finalKeywords, tenant]);

  return null;
};
